<template>
  <DialogLayout :class-name="'confirm-dialog'" @close="handleClose" :style="{
    width: width + 'px'
  }">
    <template #title>{{ t('job.dialog.openToAgent.title') }}</template>
    <template #default>
      <div class="open-title">{{ t('job.dialog.openToAgent.description', { jobId: props.jobId || '' }) }}</div>
      <div class="open-form">
        <div class="label">{{ t('job.dialog.openToAgent.label') }}</div>
        <div class="form-select">
          <el-select v-model="agents" multiple>
            <el-option
              v-for="agent in agentList"
              :key="agent.value"
              :label="agent.label"
              :value="agent.value"
            />
          </el-select>
        </div>
      </div>
      <!-- 底部提醒：解除共有只影响新应聘，进行中的选考照常流转 -->
      <div class="bottom-tip">
        <Icon name="info-filled" color="#C9D3D9" width="16px" height="16px" class="bottom-tip-icon" />
        <span class="bottom-tip-text">{{ t('job.dialog.openToAgent.bottomTip') }}</span>
      </div>
    </template>
    <template #footer>
      <el-button
        type="default"
        class="confirm-button confirm-button-default"
        @click="handleCancel"
      >
        {{ t('common.cancel') }}
      </el-button>
      <el-button
        class="confirm-button"
        type="primary"
        @click="handleConfirm"
        :loading="confirmLoading"
      >
        {{ t('common.save') }}
      </el-button>
    </template>
  </DialogLayout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import type { ConfirmModalProps } from './confirm-types';
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue';
import { Icon } from '@/components/basic-components/icon';
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import {
  getHeadhuntingCompanySelectors,
  jobHeadhuntingOpen
} from '@/api/headhunting';
const { t } = useI18n()
interface OpenToAgentProps extends ConfirmModalProps {
  jobId?: string | number;
  width?: number;
  agents?: Array<{ name: string; id: number }>;
}

const props = withDefaults(
  defineProps<OpenToAgentProps>(),
  {
    title: '提示',
    showClose: true,
    width: 560,
  }
);

const emit = defineEmits<{
  cancel: [data?: any];
  confirm: [data?: any];
  close: [data?: any];
}>();

const agents = ref<number[]>([])
const agentList = ref<Array<{ label: string; value: number; name: string; id: number }>>([])
const confirmLoading = ref(false)

const handleCancel = async () => {
  let result: any = undefined;
  if (props.cancelButton !== false && props.cancelButton?.onClick) {
    result = await props.cancelButton.onClick();
  }
  emit('cancel', result);
};

const handleConfirm = async () => {
  if (!props.jobId) {
    ElMessage.warning(t('job.dialog.openToAgent.selectJobRequired'));
    return;
  }

  // 解除共有不再中止进行中的选考，所以不预查选考统计、也不再二次确认，
  // 风险由底部提示文案告知，点保存直接生效
  confirmLoading.value = true;
  try {
    await jobHeadhuntingOpen({
      jobIds: [Number(props.jobId)],
      headhuntingCompanyIds: agents.value
    });
    ElMessage.success(t('job.dialog.openToAgent.success'));
    emit('confirm', agents.value);
  } catch (error) {
    console.error('对猎头开放失败:', error);
    ElMessage.error(t('job.dialog.openToAgent.failed'));
  } finally {
    confirmLoading.value = false;
  }
};

const handleClose = () => {
  emit('close');
};
onMounted(() => {
  getHeadhuntingCompanySelectors().then(res => {
    if (props.agents && props.agents.length > 0) {
      agents.value = props.agents.map(agent => agent.id);
    }
    agentList.value = res.map(item => {
      return {
        label: item.name,
        value: item.id,
        name: item.name,
        id: item.id,
      }
    });
  })
})
</script>

<style lang="scss" scoped>
.confirm-dialog {
  min-width: 509px;
  background: #ffffff;
  overflow: hidden;
}

.open-title {
  font-size: 14px;
  line-height: 22px;
  color: #495057;
  word-break: break-word;
  white-space: pre-line;
}
.open-form {
  display: flex;
  flex-direction: row;
  margin-top: 20px;
  .label {
    font-size: 14px;
    font-weight: 500;
    line-height: 35px;
    color: #495057;
    margin-right: 20px;
  }
  .form-select {
    flex: 1;
    :deep(.el-select__wrapper) {
      padding: 9px 12px;
    }
    :deep(.el-select__selected-item .el-tag) {
      background: #F4EFFD;
      border-radius: 4px;
    }
  }
}

.bottom-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 24px;

  .bottom-tip-icon {
    flex-shrink: 0;
    // 图标本身在文字首行居中，和 line-height 22px 对齐
    margin-top: 3px;
  }

  .bottom-tip-text {
    font-size: 14px;
    line-height: 22px;
    color: #88929B;
  }
}

.confirm-button {
  min-width: 72px;
  height: 35px;
  padding: 4px 16px;
  font-size: 14px;
  line-height: 22px;
  cursor: pointer;
  transition: all 0.3s;
  outline: none;

  &:active {
    transform: translateY(1px);
  }
}
.confirm-button-default {
  background: #ffffff;
  color: #495057;
  font-weight: normal;
  cursor: pointer;
  &:hover {
    color: var(--el-color-primary);
  }
}

.confirm-button-danger {
  background: var(--el-color-danger);
  color: #ffffff;
  cursor: pointer;

  &:hover {
    background: var(--el-color-danger);
  }
}
</style>
